import React, { useState } from 'react';
import { SearchIcon, ChevronLeftIcon, ChevronRightIcon, FilterIcon } from 'lucide-react';
export interface Column<T> {
  key: string;
  label: string;
  render?: (item: T) => React.ReactNode;
}
interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  searchPlaceholder?: string;
  onRowClick?: (item: T) => void;
  actions?: (item: T) => React.ReactNode;
  keyExtractor: (item: T) => string;
}
export function DataTable<T>({
  columns,
  data,
  searchPlaceholder = 'Search...',
  onRowClick,
  actions,
  keyExtractor
}: DataTableProps<T>) {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  // Simple search across all string values
  const filteredData = data.filter(item => {
    if (!searchTerm) return true;
    return Object.values(item as any).some(val => String(val).toLowerCase().includes(searchTerm.toLowerCase()));
  });
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  return <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden flex flex-col h-full" data-id="element-171">
      {/* Toolbar */}
      <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-gray-50" data-id="element-172">
        <div className="relative max-w-md w-full" data-id="element-173">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-174">
            <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-175" />
          </div>
          <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder={searchPlaceholder} value={searchTerm} onChange={e => {
          setSearchTerm(e.target.value);
          setCurrentPage(1);
        }} data-id="element-176" />
        </div>
        <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-177">
          <FilterIcon className="h-4 w-4 mr-2 text-gray-500" data-id="element-178" />
          Filters
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto flex-1" data-id="element-179">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-180">
          <thead className="bg-gray-50 sticky top-0 z-10" data-id="element-181">
            <tr data-id="element-182">
              {columns.map(col => <th key={col.key} scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-183">
                  {col.label}
                </th>)}
              {actions && <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-184">
                  Actions
                </th>}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-185">
            {paginatedData.length > 0 ? paginatedData.map(item => <tr key={keyExtractor(item)} onClick={() => onRowClick && onRowClick(item)} className={onRowClick ? 'cursor-pointer hover:bg-gray-50 transition-colors' : ''} data-id="element-186">
                  {columns.map(col => <td key={col.key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-187">
                      {col.render ? col.render(item) : String((item as any)[col.key] || '-')}
                    </td>)}
                  {actions && <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={e => e.stopPropagation()} data-id="element-188">
                      {actions(item)}
                    </td>}
                </tr>) : <tr data-id="element-189">
                <td colSpan={columns.length + (actions ? 1 : 0)} className="px-6 py-12 text-center text-gray-500 text-sm" data-id="element-190">
                  No records found.
                </td>
              </tr>}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="bg-white px-4 py-3 border-t border-gray-200 flex items-center justify-between sm:px-6" data-id="element-191">
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-192">
          <div data-id="element-193">
            <p className="text-sm text-gray-700" data-id="element-194">
              Showing{' '}
              <span className="font-medium" data-id="element-195">
                {filteredData.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1}
              </span>{' '}
              to{' '}
              <span className="font-medium" data-id="element-196">
                {Math.min(currentPage * itemsPerPage, filteredData.length)}
              </span>{' '}
              of <span className="font-medium" data-id="element-197">{filteredData.length}</span>{' '}
              results
            </p>
          </div>
          <div data-id="element-198">
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-199">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400" data-id="element-200">
                <span className="sr-only" data-id="element-201">Previous</span>
                <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-202" />
              </button>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || totalPages === 0} className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400" data-id="element-203">
                <span className="sr-only" data-id="element-204">Next</span>
                <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-205" />
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>;
}